import React, { useEffect, useState } from "react";
import { all_routes } from "../../../router/all_routes";
import PageHeader from "../../../../core/common/crmComponents/PageHeader";
import LoaderComponent from "../../../../core/common/crmComponents/LoaderComponent";
import { useLocation, useParams } from "react-router";
import DealDetailsHeader from "../components/DealDetailsHeader";
import DealDetailsSidebar from "../components/DealDetailsSidebar";
import { useDealData } from "../hooks/useDealData";
import DealPipelineStatus from "../components/DealPipelineStatus";
import DisplayTimeline from "../../../../core/common/crmComponents/DisplayTimeline";
import { MODULES } from "../../../../core/data/constants/moduleConstants";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { fetchTimelineData } from "../../../../utils/helpers/apiHelpers";
import { useDispatch, useSelector } from "react-redux";
import { setHasNewFilters } from "../../../../core/data/redux/filterSlice";
import { getAllUrlParams } from "../../../../utils/helpers/setAndGetUrlParams";
import { generateQueryParams } from "../../../../utils/helpers/generateQueryParams";
import { useCachedDealData } from "../hooks/getCachedDealData";
import queryClient from "../../../../config/queryClient";
import { setReloading } from "../../../../core/data/redux/moduleDataSlice";

const DealsDetails = () => {
  const route = all_routes;
  const { id: dealId } = useParams();
  const dispatch = useDispatch();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const initialForceReload = searchParams.get("shouldRefetch") === "true";

  const [forceReload, setForceReload] = useState(false);
  const { dealData, isLoading, isError, error } = useCachedDealData(
    dealId,
    null,
    initialForceReload || forceReload
  );

  const reloading = useSelector((state) => state.moduleData.reloading);
  const [view, setView] = useState("overview"); // State for view

  useEffect(() => {
    dispatch(setHasNewFilters());
  }, []);

  useEffect(() => {
    if (reloading) {
      setForceReload(true);
      queryClient.invalidateQueries(["deal", dealId]);
      dispatch(setReloading(false));
    }
  }, [reloading, setForceReload]);

  useEffect(() => {
    if (forceReload) {
      setForceReload(false);
    }
  }, [forceReload]);

  const {
    data: timelineData,
    isLoading: timelineLoading,
    isError: timelineError,
  } = useQuery({
    queryKey: ["timelineData", dealId],
    queryFn: () => fetchTimelineData(dealData?.timeline),
    enabled: !!dealData?.timeline && view === "timeline",
    staleTime: Infinity, // Keep the data fresh indefinitely
  });

  if (timelineError) return <div>Error loading timeline data.</div>;

  const renderContent = () => {
    if (view === "overview") {
      return (
        <>
          <DealPipelineStatus dealData={dealData} />
          <DealDetailsSidebar dealData={dealData} />
        </>
      );
    } else if (view === "timeline") {
      return timelineLoading ? (
        <LoaderComponent />
      ) : (
        <>
          {timelineData && Object.keys(timelineData).length > 0 && (
            <DisplayTimeline
              timeline={timelineData}
              selectedData={dealData}
              moduleName={MODULES.DEALS}
            />
          )}
        </>
      );
    }
  };

  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              {/* Page Header */}
              <PageHeader pageName="Deal Overview" moduleName="deal" />
              {/* /Page Header */}
            </div>
          </div>
          <div className="row">
            {isLoading ? (
              <LoaderComponent />
            ) : isError ? (
              <div className="col-md-12">
                <div
                  className="alert alert-danger text-capitalize"
                  role="alert"
                >
                  {error?.response?.data?.msg}
                </div>
              </div>
            ) : (
              <DealDetailsHeader
                dealData={dealData}
                view={view}
                setView={setView}
              />
            )}
          </div>
          <div className="row">{renderContent()}</div>
        </div>
      </div>
    </>
  );
};

export default DealsDetails;
